<template>
  <master-layout pageTitle="Business Profile">
    <div>
      <div class="chat-toolbar">
        <div class="toolbar-start">
          <VIcon name="arrow-left-line" :size="-4" @click="() => $router.go(-1)" class="backBttn cursorPointer"></VIcon>
          <h2 class="vtext-page-title--regular vml-20">Business settings</h2>
        </div>
        <div class="toolbar-end">
          <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" :label="commonLoader ? 'Saving...' : 'Save'" :disabled="commonLoader || !hasUnsavedChanges" @click="saveUserChanges" ></VButton>
        </div>
      </div>
      <div class="vgrid--no-padding gridMargin">
        <div class="vrow">
          <!-- Left Column -->
          <div class="vcol vcol--no-padding vcol-xl-8 vcol-lg-8 vcol-md-8 vcol-sm-12 vcol-xs-12">
            <!-- Account Details -->
            <div class="model-sections vmb-20 custMarginRight">
              <div class="cust-brdr sectionPadding">
                <h2 class="vtext-card-title--light">Account details</h2>
              </div>
              <div class="sectionPadding">
                <div class="optionsContainer cust-brdr">
                  <p class="vtext-subtitle vpb-10">Account owner name (Max 30 characters)</p>
                  <div class="counter-wrapper">
                    <VTextField v-model="userAccount.name" aria-label="store name" :maxlength="30" placeholder="Name" appearance="ghost" class="vivid-input"></VTextField>
                    <div class="custom-counter">
                      {{ (userAccount.name || '').length }}/30
                    </div>
                  </div>
                </div>
                <div class="vmb-20">
                  <p class="vtext-subtitle vpb-10">Account Unique ID</p>
                  <VTextField v-model="userAccount.referallshopName" :readonly="true" aria-label="referall store name" placeholder="Link Address" appearance="ghost" class="vivid-input"></VTextField>
                </div>
                <div>
                  <p class="vtext-subtitle vpb-10">API Key</p>
                  <VTextField v-model="userAccount.api_key" :readonly="true" aria-label="api key" placeholder="API Key" appearance="ghost" class="vivid-input vivid-input--readonly"></VTextField>
                </div>
              </div>
            </div>

            <!-- Business Address -->
            <div class="model-sections vmb-20 custMarginRight">
              <div class="cust-brdr sectionPadding">
                <h2 class="vtext-card-title--light">Business address</h2>
              </div>
              <div class="sectionPadding">
                <div class="flex-between vmb-20">
                  <div class="flex100P">
                    <p class="vtext-subtitle vpb-10">Address line 1</p>
                    <VTextField v-model="address.addressLine1" aria-label="address Line 1" placeholder="Address Line 1" appearance="ghost" class="vivid-input"></VTextField>
                  </div>
                  <div class="flex100P">
                    <p class="vtext-subtitle vpb-10">Address line 2</p>
                    <VTextField v-model="address.addressLine2" aria-label="address Line 2" placeholder="Address Line 2" appearance="ghost" class="vivid-input"></VTextField>
                  </div>
                </div>
                <div class="flex-between vmb-20">
                  <div class="flex100P">
                    <p class="vtext-subtitle vpb-10">City</p>
                    <VTextField v-model="address.city" aria-label="city" placeholder="City" appearance="ghost" class="vivid-input"></VTextField>
                  </div>
                  <div class="flex100P">
                    <p class="vtext-subtitle vpb-10">State / Province</p>
                    <VTextField v-model="address.state" aria-label="state" placeholder="State/Province" appearance="ghost" class="vivid-input"></VTextField>
                  </div>
                  <div class="flex100P">
                    <p class="vtext-subtitle vpb-10">Postal code</p>
                    <div>
                      <VTextField v-model="address.pincode" aria-label="Postal code" placeholder="Postal code" appearance="ghost" class="vivid-input"></VTextField>
                    </div>
                  </div>
                </div>
                <div class="flex100P">
                  <p class="vtext-subtitle vpb-10">Country</p>
                  <VTextField v-model="address.country" aria-label="country" placeholder="Country" appearance="ghost" class="vivid-input"></VTextField>
                </div>
              </div>
            </div>

            <!-- Timezone -->
            <div class="model-sections vmb-20 custMarginRight">
              <div class="cust-brdr sectionPadding">
                <h2 class="vtext-card-title--light vpb-10">Timezone</h2>
                <p class="vtext-subtitle">Set the timezone where your business is located</p>
              </div>
              <div class="sectionPadding">
                <p class="vtext-subtitle vpb-10 vmb-10">Timezone</p>
                <timezone-options v-model="userAccount.timezone" class="slctContainer" />
              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="vcol vcol--no-padding vcol-xl-4 vcol-lg-4 vcol-md-4 vcol-sm-12 vcol-xs-12">
            <!-- Primary Contact Number -->
            <div class="model-sections vmb-20">
              <div class="cust-brdr sectionPadding">
                <h2 class="vtext-card-title--light vpb-10">Primary contact number</h2>
                <p class="vtext-subtitle">Your primary contact number (Your customers will not see this if you have entered a number for support)</p>
              </div>
              <div class="sectionPadding">
                <p class="vtext-subtitle vpb-10"> Primary contact no</p>
                <mobile-input v-model:countryCode="userAccount.mobilecountrycode" v-model:mobile="userAccount.mobileNumber" maxlength="15" placeholder="Phone number"></mobile-input>
              </div>
            </div>

            <!-- Business Contact Details -->
            <div class="model-sections vmb-20">
              <div class="cust-brdr sectionPadding">
                <h2 class="vtext-card-title--light">Business contact details</h2>
              </div>
              <div class="sectionPadding">
                <div v-if="!userAccount.business_email_verified" class="info-box vmb-20">
                  <VIcon name="info-line" :size="-5" connotation="cta" class="infoIcon"></VIcon>
                  <p class="vtext-body">Please verify your business email.</p>
                </div>
                <div class="vmb-20">
                  <div class="flex-start vpb-10">
                    <p class="vtext-subtitle">Business email</p>
                    <HelpTooltip>We will use this information to contact you.</HelpTooltip>
                  </div>
                  <div class="flex-between gap10P">
                    <VTextField type="email" v-model="userAccount.business_email" @change="validate(userAccount.business_email)" aria-label="Business email" placeholder="Business Email" appearance="ghost" class="vivid-input"></VTextField>
                    <VButton v-if="showBusinessVerifyButton" class="vvd-btn-primary" connotation="cta" appearance="filled" label="Verify" style="height: 40px;" @click="handleVerify('business')"></VButton>
                    <VButton v-else-if="showBusinessUpdateButton" class="vvd-btn-primary" connotation="cta" appearance="filled" label="Update" style="height: 40px;" :disabled="isBusinessButtonDisabled" @click="updateAccountDetails('business')"></VButton>
                  </div>
                </div>
                <div>
                  <div class="flex-start vpb-10">
                    <p class="vtext-subtitle">Business phone no</p>
                    <HelpTooltip>We will use this information to contact you.</HelpTooltip>
                  </div>
                  <mobile-input v-model:countryCode="userAccount.business_country_code" v-model:mobile="userAccount.business_contact" maxlength="15" placeholder="Phone number"></mobile-input>
                </div>
              </div>
            </div>

            <!-- Customer Support Contact -->
            <div class="model-sections vmb-20">
              <div class="cust-brdr sectionPadding">
                <h2 class="vtext-card-title--light">Contact details for customer support</h2>
              </div>
              <div class="sectionPadding">
                <div v-if="!userAccount.support_email_verified" class="info-box vmb-20">
                  <VIcon name="info-line" :size="-5" connotation="cta" class="infoIcon"></VIcon>
                  <p class="vtext-body">Please verify your support email.</p>
                </div>
                <div class="vmb-20">
                  <p class="vtext-subtitle vpb-10">Support email</p>
                  <div class="flex-between gap10P">
                    <VTextField type="email" v-model="userAccount.support_email" @change="validate(userAccount.support_email)" aria-label="email" placeholder="Support email" appearance="ghost" class="vivid-input"></VTextField>
                    <VButton v-if="showSupportVerifyButton" class="vvd-btn-primary" connotation="cta" appearance="filled" label="Verify" style="height: 40px;" @click="handleVerify('support')"></VButton>
                    <VButton v-else-if="showSupportUpdateButton" class="vvd-btn-primary" connotation="cta" appearance="filled" label="Update" style="height: 40px;" :disabled="isSupportButtonDisabled" @click="updateAccountDetails('support')"></VButton>
                  </div>
                </div>
                <div>
                  <p class="vtext-subtitle vpb-10">Support phone no</p>
                  <mobile-input v-model:countryCode="userAccount.support_country_code" v-model:mobile="userAccount.support_contact" maxlength="15" placeholder="Phone number"></mobile-input>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Disable Account Footer -->
      <div v-if="isAdmin" class="footer">
        <div class="flex-between">
          <p class="vtext-body">Disable Account ?</p>
          <VButton class="vvd-btn-danger-outline" connotation="announcement" appearance="outlined" label="Disable account" @click="() => deleteAccountMode = true"></VButton>
        </div>
      </div>
    </div>

    <!-- Disable Account Modal -->
    <VDialog :open="deleteAccountMode" modal @close="deleteAccountMode = false">
      <template #body>
        <div class="exportImgContainer vmb-20">
          <VIcon name="delete-line" :size="-5" class="customer_delete"></VIcon>
          <p class="vtext-modal-title text-center">Are you sure ?</p>
          <p class="vtext-subtitle text-center">Once you disable your account, all your account data along with applied subscription will be disabled and can't be recovered. Please enter 'DISABLE' to disable account.</p>
        </div>
        <VTextField v-model="disable_text" placeholder="Enter 'DISABLE' to disable account" aria-label="Enter 'DISABLE' to disable account" @keyup.enter="confirmDelete()" appearance="ghost" class="vivid-input flex100P"></VTextField>
      </template>
      <template #action-items>
        <VButton class="vvd-btn-primary flex100P" connotation="cta" appearance="filled" label="Yes, disable my account" :disabled="disable_text !== 'DISABLE'" @click="confirmDelete()"></VButton>
        <VButton class="vvd-btn-cancel flex100P" appearance="filled" label="No, keep my account" @click="deleteAccountMode = false; disable_text = '';"></VButton>
      </template>
    </VDialog>

    <!-- Verify OTP Modal -->
    <VDialog :open="verifyOTPModal" :headline="'Verify ' + typeofEmail + ' email'" modal @close="verifyOTPModal = false">
      <template #body>
        <div class="vmb-20">
          <div class="flex-between vpb-10 gap10P">
            <p class="vtext-subtitle">Verification code</p>
            <a @click="resendOTP" :class="{ disabled: isResendDisabled }" :style="{ cursor: isResendDisabled ? 'not-allowed' : 'pointer', opacity: isResendDisabled ? 0.5 : 1 }"> {{ isResendDisabled ? `Resend code in ${formatTime(remainingTime)}` : 'Resend code' }} </a>
          </div>
          <InputNumber name="otp" maxlength="8" @keyup.enter="confirmOTP" v-model="otp_text" :allowDecimal="false" placeholder="Enter verification code" ariaLabel="otp"> </InputNumber>
        </div>
      </template>
      <template #action-items>
        <VButton class="vvd-btn-primary vvd-btn-block" connotation="cta" appearance="filled" label="Verify" :disabled="otp_text == ''" @click="confirmOTP()"></VButton>
      </template>
    </VDialog>
  </master-layout>
</template>

<script>
import timezoneOptions from '/src/components/timezoneOptions';
import MobileInput from '/src/components/mobileInput.vue';
import InputNumber from "/src/components/inputNumber.vue";
import HelpTooltip from "/src/components/HelpTooltip.vue";
import apiInterceptor from '@/shared/apiInterceptor';
import { queryClient } from '@/shared/queryClient';
import { userDataQueryOptions } from '@/composables/useUserData';
import { useAuthStore } from '@/stores/auth';
import { useUIStore } from '@/stores/ui';
import { validateEmail, validatePhoneNumber } from '@/helpers/validation.helpers';
import { environment } from '@/shared/env';

export default {
  components: { timezoneOptions, MobileInput, InputNumber, HelpTooltip },
  data() {
    return {
      userAccount: {
        name: undefined,
        referallshopName: undefined,
        mobilecountrycode: undefined,
        mobileNumber: undefined,
        business_contact: undefined,
        business_country_code: undefined,
        business_email: undefined,
        support_contact: undefined,
        support_country_code: undefined,
        support_email: undefined,
        timezone: undefined,
        business_email_verified: undefined,
        support_email_verified: undefined,
        api_key: undefined
      },
      address: {
        addressLine1: undefined,
        addressLine2: undefined,
        city: undefined,
        state: undefined,
        country: undefined,
        pincode: undefined
      },
      addressClone: {
        addressLine1: undefined,
        addressLine2: undefined,
        city: undefined,
        state: undefined,
        country: undefined,
        pincode: undefined
      },
      deleteAccountMode: false,
      commonLoader: false,
      disable_text: '',
      address_id: undefined,
      verifyOTPModal: false,
      otp_text: '',
      isBusinessButtonDisabled: true,
      isSupportButtonDisabled: true,
      initialBusniessEmail: '',
      initialSupportEmail: '',
      typeofEmail: '',
      isResendDisabled: false,
      remainingTime: 0,
      timer: null,
      userDataSnapshot: null,
      businessEmailLengthError: false,
      supportEmailLengthError: false
    }
  },
  mounted() {
    this.init();
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  computed: {
    user() { return useAuthStore().cleanUserData; },
    isAdmin() { return useAuthStore().isAdmin; },
    addChanged() {
      for (var prop in this.address) {
        if (this.address[prop] !== this.addressClone[prop] || (this.address[prop] === '' && this.addressClone[prop] !== '')) {
          return true;
        }
      }
      return false;
    },
    accountChanged() {
      if (!this.userDataSnapshot) return false;
      for (const prop in this.userAccount) {
        if (prop === 'business_email_verified' || prop === 'support_email_verified') continue;
        if (this.userAccount[prop] !== this.userDataSnapshot[prop]) return true;
      }
      return false;
    },
    hasUnsavedChanges() {
      return this.accountChanged || this.addChanged;
    },
    showBusinessVerifyButton() {
      return !this.userAccount.business_email_verified &&
        this.userAccount.business_email === this.initialBusniessEmail;
    },
    showBusinessUpdateButton() {
      return this.userAccount.business_email_verified ||
        (this.userAccount.business_email !== this.initialBusniessEmail);
    },
    showSupportVerifyButton() {
      return !this.userAccount.support_email_verified &&
      this.userAccount.support_email === this.initialSupportEmail;
    },
    showSupportUpdateButton() {
      return this.userAccount.support_email_verified ||
        (this.userAccount.support_email !== this.initialSupportEmail);
    }
  },
  watch: {
    'userAccount.business_email'(newVal) {
      this.isBusinessButtonDisabled = !newVal || newVal === this.initialBusniessEmail;
    },
    'userAccount.support_email'(newVal) {
      this.isSupportButtonDisabled = !newVal || newVal === this.initialSupportEmail;
    },
  },
  methods: {
    async logoutUser() { return useAuthStore().logoutUser(); },
    async init(fetchAddr = true) {
      let userData = this.user ?? await queryClient.fetchQuery(userDataQueryOptions);
      if (userData?.success === true && userData?.data) userData = userData.data;
      this.userDataSnapshot = userData ? { ...userData } : null;
      if (userData) {
        for (var prop in this.userAccount) {
          if (Object.prototype.hasOwnProperty.call(userData, prop)) {
            this.userAccount[prop] = userData[prop];
          }
        }
      }
      this.initialBusniessEmail = this.userAccount.business_email;
      this.initialSupportEmail = this.userAccount.support_email;
      if (fetchAddr) await this.fetchAddress();
    },
    async fetchAddress() {
      try {
        useUIStore().updateLoading({ isLoading: true });
        const fetchAddressResponse = await apiInterceptor.get( environment("VUE_APP_API_URL") + "settings/v1/account/address/" );
        if(fetchAddressResponse) {
          for(var prop in this.address) this.address[prop] = fetchAddressResponse[prop];
          this.address_id = fetchAddressResponse?.id;
          this.addressClone = JSON.parse(JSON.stringify(this.address));
        } else console.error("Address not available");
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async updateAccountDetails(type) {
      try {
        useUIStore().updateLoading({ isLoading: true });
        if (this.userAccount.business_email && this.userAccount.business_email.length >= 255) {
          this.businessEmailLengthError = true;
          useUIStore().updateLoading({ isLoading: false });
          return;
        }
        if (this.userAccount.support_email && this.userAccount.support_email.length >= 255) {
          this.supportEmailLengthError = true;
          useUIStore().updateLoading({ isLoading: false });
          return;
        }
        let reqBody = {};
        // Mandatory fields that must never be sent as empty.
        const mandatoryAccountFields = new Set(['name', 'mobileNumber', 'mobilecountrycode']);
        for(var prop in this.userAccount) {
          if(this.userAccount[prop] !== this.userDataSnapshot[prop]) {
            const val = this.userAccount[prop];
            // For optional fields, a cleared value must be sent as an empty
            // string "" (NOT null). The backend ignores null and keeps the
            // previous value, but honours "" as an explicit clear.
            if (!mandatoryAccountFields.has(prop) && (val === undefined || val === null)) {
              reqBody[prop] = '';
            } else {
              reqBody[prop] = val;
            }
          }
        }
        if (reqBody.mobileNumber !== undefined) {
          reqBody.mobile_number = reqBody.mobileNumber;
          delete reqBody.mobileNumber;
        }
        if (this.isUserDataValid()) {
          if (!this.userDataSnapshot) {
            useUIStore().updateToast({ open: true, message: "Account data not loaded. Please refresh and try again.", type: "danger" });
            return;
          }
          let reqBody = {};
          for(var prop in this.userAccount) if(this.userAccount[prop] !== this.userDataSnapshot[prop]) reqBody[prop] = this.userAccount[prop];
          if (reqBody.mobileNumber !== undefined) {
            reqBody.mobile_number = reqBody.mobileNumber;
            delete reqBody.mobileNumber;
          }
          await apiInterceptor.patch( environment("VUE_APP_API_URL") + "settings/v1/account/", reqBody );
          useUIStore().updateToast({ open: true, message: "Account details are saved successfully!", type: "success" })
          await queryClient.refetchQueries({ queryKey: ['account-settings'] });
          await queryClient.refetchQueries({ queryKey: ['role-details'] });
          await useAuthStore().checkToken();
          this.init(false);
          this.initialBusniessEmail = this.userAccount.business_email;
          this.initialSupportEmail = this.userAccount.support_email;
          if(type !== 'accountUpdate'){
            this.startTimer();
            this.handleVerify(type);
          }
        } else {
          useUIStore().updateToast({
            open: true,
            message: "Please enter valid contact numbers and try again",
            type: "danger"
          });
        }
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async saveUserChanges() {
      if (!this.validateForm()) return;
      if (this.addChanged) await this.saveAddressChanges();
      if (this.accountChanged) await this.updateAccountDetails('accountUpdate');
    },
    async saveAddressChanges() {
      try {
        this.commonLoader = true;
        let reqBody = {};
        // Copy all props from this.address. Address fields are all optional. A
        // cleared field is sent as an empty string "" (NOT null) — the backend
        // ignores null and keeps the previous value, but honours "" as a clear.
        for (var prop in this.address) {
          const val = this.address[prop];
          reqBody[prop] = (val === undefined || val === null) ? '' : val;
        }
        // Rename keys if they exist
        if (reqBody.addressLine1 !== undefined) {
          reqBody.address_line_1 = reqBody.addressLine1;
          delete reqBody.addressLine1;
        }
        if (reqBody.addressLine2 !== undefined) {
          reqBody.address_line_2 = reqBody.addressLine2;
          delete reqBody.addressLine2;
        }
        if(!this.address_id){
          await apiInterceptor.post( environment("VUE_APP_API_URL") + "settings/v1/account/address/", reqBody );
        } else {
          await apiInterceptor.patch( environment("VUE_APP_API_URL") + "settings/v1/account/address/" + this.address_id, reqBody );
        }
        // Re-fetch from the server as the single source of truth. Relying on the
        // save response is fragile because its key casing (camelCase vs
        // snake_case) may differ from what the form binds to, which previously
        // caused cleared fields to appear to repopulate their old value.
        await this.fetchAddress();
        useUIStore().updateToast({ open: true, message: "Business address details are saved successfully!", type: "success" })
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
      } finally {
        this.commonLoader = false;
      }
    },
    async confirmDelete() {
      try {
        useUIStore().updateLoading({ isLoading: true });
        if(this.disable_text !== 'DISABLE') {
          return useUIStore().updateToast({ open: true, message: "Please enter 'DISABLE' and try again", type: "danger" });
        }
        await apiInterceptor.delete( environment("VUE_APP_API_URL") + "settings/v1/account/");
        await useAuthStore().logoutUser();
        useUIStore().updateToast({
          open: true,
          message: "Deleted!\nYour account has been disabled successfully.",
          type: "danger"
        });
        window.location.reload();
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
      } finally {
        useUIStore().updateLoading({ isLoading: false });
        this.deleteAccountMode = false;
        this.disable_text = '';
      }
    },
    validate(email){
      if (!validateEmail(email)) {
        useUIStore().updateToast({ open: true, message: "Please enter a valid email ID", type: "danger" });
        return;
      }
    },
    handleBusinessEmailInput(event) {
      const value = event.detail.value || '';
      if (value.length >= 255) {
        this.businessEmailLengthError = true;
        this.userAccount.business_email = value.substring(0, 255);
      } else {
        this.businessEmailLengthError = false;
      }
    },
    handleSupportEmailInput(event) {
      const value = event.detail.value || '';
      if (value.length >= 255) {
        this.supportEmailLengthError = true;
        this.userAccount.support_email = value.substring(0, 255);
      } else {
        this.supportEmailLengthError = false;
      }
    },
    validateForm() {
      const account = this.userAccount;
      const isFilled = (value) => value !== undefined && value !== null && String(value).trim() !== '';
      const showError = (message) => {
        useUIStore().updateToast({ open: true, message, type: "danger" });
        return false;
      };
      const name = (account.name || '').trim();
      if (!name) return showError("Account owner name is required.");
      if (name.length > 30) return showError("Account owner name must not exceed 30 characters.");
      if (!isFilled(account.mobileNumber) || !isFilled(account.mobilecountrycode)) return showError("Primary contact number is required.");
      const primaryPhone = validatePhoneNumber(String(account.mobileNumber));
      if (primaryPhone !== true) return showError(`Primary contact number: ${primaryPhone}`);
      if (isFilled(account.business_email)) {
        if (account.business_email.length >= 255) return showError("Business email must not exceed 255 characters.");
        if (!validateEmail(account.business_email)) return showError("Please enter a valid business email.");
      }
      if (isFilled(account.support_email)) {
        if (account.support_email.length >= 255) return showError("Support email must not exceed 255 characters.");
        if (!validateEmail(account.support_email)) return showError("Please enter a valid support email.");
      }
      if (isFilled(account.business_contact)) {
        const result = validatePhoneNumber(String(account.business_contact));
        if (result !== true) return showError(`Business phone: ${result}`);
        if (!isFilled(account.business_country_code)) return showError("Please select a country code for the business phone.");
      }
      if (isFilled(account.support_contact)) {
        const result = validatePhoneNumber(String(account.support_contact));
        if (result !== true) return showError(`Support phone: ${result}`);
        if (!isFilled(account.support_country_code)) return showError("Please select a country code for the support phone.");
      }
      return true;
    },
    isUserDataValid() {
      return this.validateForm();
    },
    handleVerify(type) {
      this.verifyOTPModal = true;
      this.typeofEmail = type;
      useUIStore().updateToast({ open: true, message: "Please enter the verification code sent to you email", type: "success" });
    },
    async confirmOTP() {
      try {
        useUIStore().updateLoading({ isLoading: true });
        let reqBody = {
          verification_code: this.otp_text,
          is_support_email: this.typeofEmail == 'support' ? true : false
        };
        await apiInterceptor.post( environment("VUE_APP_API_URL") + "settings/v1/account/verify/", reqBody);
        useUIStore().updateToast({
          open: true,
          message: "Successfully verified OTP and updated the email",
          type: "success"
        });
        this.verifyOTPModal = false;
        await queryClient.refetchQueries({ queryKey: ['account-settings'] });
        await queryClient.refetchQueries({ queryKey: ['role-details'] });
        await useAuthStore().checkToken();
        this.init(false);
        this.initialBusniessEmail = this.userAccount.business_email;
        this.initialSupportEmail = this.userAccount.support_email;
        this.$nextTick(() => {
          this.isBusinessButtonDisabled = true;
          this.isSupportButtonDisabled = true;
        });
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
      } finally {
        useUIStore().updateLoading({ isLoading: false });
        this.otp_text = '';
      }
    },
    async resendOTP() {
      try {
        if (this.isResendDisabled) return;
        useUIStore().updateLoading({ isLoading: true });
        let reqBody = {
          email_id: this.typeofEmail == 'support' ? this.userAccount.support_email : this.userAccount.business_email,
          is_support_email: this.typeofEmail == 'support' ? true : false
        };
        await apiInterceptor.post( environment("VUE_APP_API_URL") + "settings/v1/account/resend-otp/", reqBody);
        useUIStore().updateToast({
          open: true,
          message: "Verification code sent successfully!",
          type: "success"
        });
        this.startTimer();
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    startTimer() {
      this.isResendDisabled = true;
      this.remainingTime = 600;
      this.timer = setInterval(() => {
        this.remainingTime--;
        if (this.remainingTime <= 0) {
          this.isResendDisabled = false;
          clearInterval(this.timer);
          this.timer = null;
        }
      }, 1000);
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  }
}
</script>

<style lang="scss" scoped src="@/resources/css/settings.scss"> </style>
<style scoped>
.chat-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--white);
  border-bottom: 1px solid var(--background-primary);
}

.toolbar-start {
  display: flex;
  align-items: center;
}

.toolbar-end {
  padding-right: 15px;
}

.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}

.vivid-input--readonly {
  background: var(--gray-01);
}
</style>
